/**
 * Plan 12, Task 4.1 — Tile marker renderer.
 *
 * buildTilePads() — colored disc pads + icon decals per tile type.
 */

import * as THREE from 'three';
import renderScales from '@shared/render-scales';
import type { BoardNode, TileType } from '../../../shared/board/types';
import { getTileSurfaceFrameForNode, TILE_PAD_HEIGHT, type SurfaceLookup } from './surface-y';

// ── Tile Pads ─────────────────────────────────────────────────────────────────
// Colored disc pads with optional PNG icon decals for each non-pathing tile.
// Colors match IDEA.md §Board Design semantics.

const TILE_PAD_COLORS: Record<string, number> = {
  start: 0x9090a0, // neutral gray-blue (spawn)
  gold: 0xe8d020, // yellow
  heal: 0x40b040, // green
  hazard: 0xe04040, // red
  item: 0x9040c0, // purple
  soul_anchor: 0xe08820, // orange (player-color when claimed; default orange)
  key_shop: 0x2fc0a0, // teal
  weapons_cache: 0xb25a35, // rust
  mystery_box: 0x5a50c8, // indigo
  event: 0x4080d0, // blue
  graveyard: 0x404040, // dark gray
};

const TILE_TYPE_TO_ICON_KEY: Record<string, string | null> = {
  start: 'start',
  gold: 'gold',
  heal: 'heal',
  hazard: 'hazard',
  item: 'item',
  soul_anchor: 'soul-anchor', // file uses hyphen
  key_shop: null,
  weapons_cache: null,
  mystery_box: null,
  event: 'event',
  graveyard: 'graveyard',
  pathing: null,
};

export interface TilePadsResult {
  readonly group: THREE.Group;
  readonly meshToTileInfo: ReadonlyMap<THREE.Object3D, { nodeId: string; tileType: TileType }>;
  getMesh(nodeId: string): THREE.Mesh | undefined;
  setVisible(nodeId: string, visible: boolean): void;
  setTint(nodeId: string, color: number | null): void;
  dispose(): void;
}

/** Hex prism geometry constants (shared between game and editor). */
const HEX_RADIUS = renderScales.tile.padRadius;
const HEX_HEIGHT = TILE_PAD_HEIGHT;

/** Per-island data needed for tile pad placement. */
export type IslandDataForPads =
  SurfaceLookup['islandData'] extends ReadonlyMap<string, infer T> ? T : never;

/**
 * Build hexagonal prism tile pads with icon decals for all non-pathing board nodes.
 *
 * Each tile is a render-scales.json-sized hexagonal prism.
 * Icon decal is a PlaneGeometry flush above the hex top face.
 * Pad Y = island surfaceY + HEX_HEIGHT / 2 so the hex sits ON the grass surface.
 *
 * @param args.nodes - Full list of board nodes (pathing nodes are skipped).
 * @param args.islandData - Per-island surfaceY map (keyed by island-N). Falls back to node.position.y if missing.
 * @param args.tileIconTextures - Map of icon key → loaded THREE.Texture (graceful — missing entries skip decal).
 * @returns TilePadsResult containing the group and a dispose() function.
 */
export function buildTilePads(args: {
  nodes: readonly BoardNode[];
  islandData: ReadonlyMap<string, IslandDataForPads>;
  bridgeTransforms?: ReadonlyMap<string, THREE.Matrix4>;
  tileIconTextures: Partial<Record<string, THREE.Texture>>;
}): TilePadsResult {
  const { nodes, islandData, bridgeTransforms, tileIconTextures } = args;
  const group = new THREE.Group();
  group.name = 'tile-pads';
  const ownedDisposables: Array<{ dispose(): void }> = [];
  const padByNodeId = new Map<string, THREE.Mesh>();
  const originalMaterials = new Map<
    string,
    { color: THREE.Color; emissive: THREE.Color; emissiveIntensity: number }
  >();
  const meshToTileInfo = new Map<THREE.Object3D, { nodeId: string; tileType: TileType }>();

  for (const node of nodes) {
    if (node.type === 'pathing') continue;

    const surfaceFrame = getTileSurfaceFrameForNode(node, { islandData, bridgeTransforms });

    const color = TILE_PAD_COLORS[node.type] ?? 0x808080;

    // Hex prism pad: 6-sided cylinder, render-scales.json height, flat shading
    const padGeo = new THREE.CylinderGeometry(HEX_RADIUS, HEX_RADIUS, HEX_HEIGHT, 6);

    // Single material: icon texture wraps all faces (top, sides, bottom).
    // Sides will appear stretched — that is intentional per design decision.
    const iconKey = TILE_TYPE_TO_ICON_KEY[node.type];
    const iconTex = iconKey != null ? (tileIconTextures[iconKey] ?? null) : null;

    let padMat: THREE.MeshStandardMaterial;
    if (iconTex != null) {
      padMat = new THREE.MeshStandardMaterial({
        map: iconTex,
        color: 0xffffff, // white tint so map shows true colors
        emissiveMap: iconTex,
        emissive: new THREE.Color(0xffffff),
        emissiveIntensity: 0.15,
      });
    } else {
      // Fallback: solid type color when no icon for this tile type
      padMat = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.25,
        flatShading: true,
      });
    }
    const pad = new THREE.Mesh(padGeo, padMat);
    // CylinderGeometry axis is Y; bridge-hosted tiles inherit the bridge frame.
    pad.quaternion.copy(surfaceFrame.quaternion);
    pad.position.copy(surfaceFrame.position).addScaledVector(surfaceFrame.up, HEX_HEIGHT / 2);
    pad.name = `tile-pad:${node.id}`;
    group.add(pad);
    padByNodeId.set(node.id, pad);
    originalMaterials.set(node.id, {
      color: padMat.color.clone(),
      emissive: padMat.emissive.clone(),
      emissiveIntensity: padMat.emissiveIntensity,
    });
    meshToTileInfo.set(pad, { nodeId: node.id, tileType: node.type });
    ownedDisposables.push(padGeo, padMat);
    // No separate icon plane — texture is mapped onto all hex faces
  }

  let disposed = false;

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const d of ownedDisposables) {
      d.dispose();
    }
  }

  function setVisible(nodeId: string, visible: boolean): void {
    const pad = padByNodeId.get(nodeId);
    if (pad != null) {
      pad.visible = visible;
    }
  }

  function getMesh(nodeId: string): THREE.Mesh | undefined {
    return padByNodeId.get(nodeId);
  }

  function setTint(nodeId: string, color: number | null): void {
    const pad = padByNodeId.get(nodeId);
    if (pad == null || !(pad.material instanceof THREE.MeshStandardMaterial)) return;

    const mat = pad.material;
    if (color == null) {
      const original = originalMaterials.get(nodeId);
      if (original == null) return;
      mat.color.copy(original.color);
      mat.emissive.copy(original.emissive);
      mat.emissiveIntensity = original.emissiveIntensity;
      return;
    }

    mat.color.setHex(color);
    mat.emissive.setHex(color);
    mat.emissiveIntensity = 0.45;
  }

  return { group, meshToTileInfo, getMesh, setVisible, setTint, dispose };
}
